import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { MdClose } from 'react-icons/md';
import {
  setPositionEditing, selectPositionEditing, selectPositionEditingParent,
  fetchAssetTypes, selectFetchAssetTypeStatus,
  selectAssetTypeIdsAcoes, selectAssetTypeIdsTitulosGoverno, selectAssetTypeIdsFiiFiagro, selectAssetTypeIdsETF, selectAssetTypeIdsBDR,
  resetSecurities,
  resetLastPrice,
  selectAssetTypeIdsFundos,
} from './edit-position-slice';
import EditPositionDefault from './edit-position-default';
import EditPositionUserCustom from './edit-position-user-custom';
import EditPositionFunds from './edit-position-funds';

type ConsolidatedAssetType = '' | 'fundos_investimento' | 'acoes' | 'fii_fiagro' | 'bdr' | 'etf' | 'bonds' | 'outros';
const consolidatedAssetTypeEmpty: ConsolidatedAssetType = '';
const consolidatedAssetTypeFundos: ConsolidatedAssetType = 'fundos_investimento';
const consolidatedAssetTypeAcoes: ConsolidatedAssetType = 'acoes';
const consolidatedAssetTypeBDR: ConsolidatedAssetType = 'bdr';
const consolidatedAssetTypeETF: ConsolidatedAssetType = 'etf';
const consolidatedAssetTypeFiiFiagro: ConsolidatedAssetType = 'fii_fiagro';
const consolidatedAssetTypeGoverno: ConsolidatedAssetType = 'bonds';
const consolidatedAssetTypeOutros: ConsolidatedAssetType = 'outros';
const editPositionFundsAssetTypes: ConsolidatedAssetType[] = [
  consolidatedAssetTypeFundos,
];
const editPositionDefaultAssetTypes: ConsolidatedAssetType[] = [
  consolidatedAssetTypeAcoes,
  consolidatedAssetTypeBDR,
  consolidatedAssetTypeETF,
  consolidatedAssetTypeFiiFiagro,
  consolidatedAssetTypeGoverno,
];
const editPositionUserCustomAssetTypes: ConsolidatedAssetType[] = [
  consolidatedAssetTypeOutros,
];

/**
 * Form to add and edit a position
 */
const EditPosition: React.FC = () => {
  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const portfolioPositionEditing = useAppSelector(selectPositionEditing);
  const portfolioPositionEditingParent = useAppSelector(selectPositionEditingParent);
  const fetchAssetTypeStatus = useAppSelector(selectFetchAssetTypeStatus);
  const assetTypeIdsFundos = useAppSelector(selectAssetTypeIdsFundos);
  const assetTypeIdsAcoes = useAppSelector(selectAssetTypeIdsAcoes);
  const assetTypeIdsBDR = useAppSelector(selectAssetTypeIdsBDR);
  const assetTypeIdsFiiFiagro = useAppSelector(selectAssetTypeIdsFiiFiagro);
  const assetTypeIdsETF = useAppSelector(selectAssetTypeIdsETF);
  const assetTypeIdsGoverno = useAppSelector(selectAssetTypeIdsTitulosGoverno);
  const isNewPosition = portfolioPositionEditing?.id ? false : true;

  const [assetType, setAssetType] = useState<ConsolidatedAssetType>(consolidatedAssetTypeEmpty);

  const { register, formState: { errors } } = useForm({
    values: {
      assetType
    }
  });

  // React useEffect: applies after rendering
  useEffect(() => {
    if (fetchAssetTypeStatus === 'idle') {
      const execute = async () => {
        const accessToken = await getAccessTokenSilently();
        dispatch(fetchAssetTypes({ accessToken }));
      }
      execute();
    }
  });

  const closeEditHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAssetType('');
    dispatch(setPositionEditing({ position: undefined, parent: undefined }));
    dispatch(resetLastPrice());
  }

  function assetTypeChangedHandler(e: React.ChangeEvent<HTMLSelectElement>): void {
    setAssetType(e.target.value as ConsolidatedAssetType);
    dispatch(resetSecurities());
  }

  const getConsolidateAssetType = (assetTypeId: string): ConsolidatedAssetType => {
    if (assetTypeIdsFundos.includes(assetTypeId)) {
      return consolidatedAssetTypeFundos;
    } else if (assetTypeIdsAcoes.includes(assetTypeId)) {
      return consolidatedAssetTypeAcoes;
    } else if (assetTypeIdsBDR.includes(assetTypeId)) {
      return consolidatedAssetTypeBDR;
    } else if (assetTypeIdsFiiFiagro.includes(assetTypeId)) {
      return consolidatedAssetTypeFiiFiagro;
    } else if (assetTypeIdsETF.includes(assetTypeId)) {
      return consolidatedAssetTypeETF;
    } else if (assetTypeIdsGoverno.includes(assetTypeId)) {
      return consolidatedAssetTypeGoverno;
    } else {
      return consolidatedAssetTypeOutros;
    }
  }

  function assetTypeIdsSelected() {
    switch (assetType) {
      case consolidatedAssetTypeFundos: {
        return assetTypeIdsFundos;
      }
      case consolidatedAssetTypeAcoes: {
        return assetTypeIdsAcoes;
      }
      case consolidatedAssetTypeBDR: {
        return assetTypeIdsBDR;
      }
      case consolidatedAssetTypeFiiFiagro: {
        return assetTypeIdsFiiFiagro;
      }
      case consolidatedAssetTypeETF: {
        return assetTypeIdsETF;
      }
      case consolidatedAssetTypeGoverno: {
        return assetTypeIdsGoverno;
      }
      case consolidatedAssetTypeOutros: {
        return [];
      }
    }
    return [];
  }

  function loadAllSecurities() {
    switch (assetType) {
      case consolidatedAssetTypeAcoes: {
        return false;
      }
      case consolidatedAssetTypeBDR: {
        return false;
      }
      case consolidatedAssetTypeFiiFiagro: {
        return false;
      }
      case consolidatedAssetTypeETF: {
        return false;
      }
      case consolidatedAssetTypeGoverno: {
        return true;
      }
    }
    return false;
  }

  useEffect(() => {
    if (portfolioPositionEditing?.id) {
      setAssetType(getConsolidateAssetType(portfolioPositionEditing?.security?.assetType?.id ?? ''));
    }
  }, [portfolioPositionEditing])

  return (
    <div className='flex flex-col w-full p-4 z-40 opacity-80'>
      <div className='flex justify-end'>
        <div onMouseDown={closeEditHandler} className='cursor-pointer'>
          <MdClose size={20} />
        </div>
      </div>
      <form className='w-full flex flex-col items-center text-slate-700'>
        <div className='flex flex-col w-full mb-6 border-b border-neutral-300 pb-4 text-lg lg:text-base text-center'>
          <label>{isNewPosition ? 'Criar Posição' : 'Editar Posição'}</label>
          <label>{isNewPosition ? `(na categoria "${portfolioPositionEditingParent?.category.name}")` : ''}</label>
        </div>

        <div className='flex flex-col w-full mb-4'>
          <label className='ml-1'>Tipo de ativo</label>
          <select disabled={!isNewPosition} className='px-2 py-1 rounded-xl mt-1 border border-gray-400 outline-slate-800 bg-white disabled:bg-gray-300 disabled:text-gray-500 opacity-100'
            {...register("assetType", { required: true })}
            onChange={assetTypeChangedHandler}
          >
            <option className='hidden' value={consolidatedAssetTypeEmpty}>-- escolha --</option>
            <option value={consolidatedAssetTypeFundos}>Fundos de Investimento</option>
            <option value={consolidatedAssetTypeAcoes}>Ações</option>
            <option value={consolidatedAssetTypeFiiFiagro}>FII e FIAGRO</option>
            <option value={consolidatedAssetTypeETF}>ETF</option>
            <option value={consolidatedAssetTypeBDR}>BDR</option>
            <option value={consolidatedAssetTypeGoverno}>Títulos do Governo (tesouro)</option>
            <option value={consolidatedAssetTypeOutros}>Outros (preficicado por você)</option>
          </select>
          <ErrorMessage errors={errors} name="assetType" render={({ message }) => <div className='text-red-500 text-sm font-semibold'>{message}</div>} />
        </div>
      </form >
      {
        editPositionFundsAssetTypes.includes(assetType) && <EditPositionFunds assetTypeIds={assetTypeIdsSelected()} />
      }
      {
        editPositionDefaultAssetTypes.includes(assetType) && <EditPositionDefault assetTypeIds={assetTypeIdsSelected()} loadAllSecurities={loadAllSecurities()} />
      }
      {
        editPositionUserCustomAssetTypes.includes(assetType) && <EditPositionUserCustom />
      }
    </div>
  );
}

export default EditPosition;